import { useEffect, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { sharedSwUpdateController } from '@/app/swUpdate';

/**
 * «Доступна новая версия» — rules 3 and 6 of the update protocol.
 *
 * Rendered from the root route so it reaches every screen, pushed ones included.
 *
 * **Rule 6, and why it is not optional.** The toast is suppressed anywhere under `/order`. A
 * person part-way through the wizard has an address typed, cars chosen and a time picked, none of
 * it on the server yet; a reload throws that away. The prompt is not urgent — the app keeps
 * working on the old build — so it waits until they are somewhere a reload costs nothing. The
 * route does not exist yet (wave 3), and the prefix is written now precisely so that it cannot be
 * forgotten when it does.
 *
 * The toast never auto-dismisses on a timer. It is not an announcement, it is an offer, and an
 * offer that vanishes while being read is worse than no offer.
 */

/** Anywhere a reload would destroy work in progress. */
const CRITICAL_PATH = /^\/order(\/|$)/;

/**
 * Rule 6, as a function, so it can be tested without a router.
 *
 * It cannot be exercised end-to-end from an unauthenticated browser: `/order/*` is behind the
 * protected layout, so an anonymous visit redirects to `/login` — where the toast *should* appear.
 * The rule itself is this one line, and this is where it is guaranteed.
 */
export function suppressesUpdateToast(pathname: string): boolean {
  return CRITICAL_PATH.test(pathname);
}

/** Tab roots keep a bar at the bottom; a toast pinned over it would cover navigation. */
const TAB_PATHS = new Set(['/', '/history', '/profile']);

export function UpdateToast() {
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  // The controller outlives this component deliberately — see `sharedSwUpdateController`. Only
  // the subscription is tied to the mount.
  const controller = sharedSwUpdateController();

  useEffect(() => controller.subscribe(setReady), [controller]);

  if (!ready || hidden || suppressesUpdateToast(pathname)) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-50 mx-auto flex justify-center px-4"
      // `mx-auto` + the app's own width: the toast belongs to the column, not to the window.
      style={{
        maxWidth: 'var(--app-width)',
        bottom: TAB_PATHS.has(pathname)
          ? 'calc(env(safe-area-inset-bottom, 0px) + 74px)'
          : 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
      }}
    >
      {/* `flex-wrap` и кнопки одной группой ЗАЧЕМ: обе кнопки были `shrink-0`, и на 320 CSS-px
          тексту оставалось ~86 px при нужных 175 — заголовок рассыпался на три строки, подпись ещё
          на три. При системном увеличении шрифта кнопки переставали помещаться и «Позже» ложилось
          прямо поверх «Обновление займёт пару секунд». Теперь на узком плашка честно встаёт в две
          строки: текст сверху, кнопки под ним. */}
      <div
        className="pointer-events-auto flex w-full max-w-[440px] flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-line bg-surface px-3.5 py-3"
        style={{ boxShadow: 'var(--shadow-card)' }}
      >
        {/* `basis-[11rem]` — порог переноса: пока тексту хватает 176 px, всё стоит в одну строку. */}
        <div className="min-w-0 flex-1 basis-[11rem]">
          <strong className="block text-[13.5px] font-bold leading-[1.3]">
            Доступна новая версия
          </strong>
          <span className="text-[12.5px] leading-[1.28] text-ink-soft">
            Обновление займёт пару секунд
          </span>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setHidden(true)}
            className="shrink-0 rounded-sm px-2.5 py-2 text-[13px] font-semibold text-ink-soft"
          >
            Позже
          </button>
          <button
            type="button"
            onClick={() => controller.apply()}
            className="shrink-0 rounded-sm bg-accent px-3.5 py-2 text-[13px] font-bold text-accent-ink"
          >
            Обновить
          </button>
        </div>
      </div>
    </div>
  );
}
